import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { accountingService } from '@/services/accounting-service';
import type { AccountTag } from '@/types/models';

/**
 * Account-tag registry hooks (migration 170). Deliberately kept in their OWN
 * module — importing only react-query + the service — so leaf components like
 * AccountFlagBadges can use them without dragging the heavy `use-accounting`
 * chain (which pulls `@/components/auth` → expo-router) into lightweight,
 * router-free unit tests (e.g. report-columns / accounts-report).
 */

const ACCOUNT_TAGS_KEY = ['accounting', 'account-tags'];

/** The global tag registry — labels, colors, and pause/late-fee behavior. */
export function useAccountTags() {
	return useQuery({
		queryKey: ACCOUNT_TAGS_KEY,
		queryFn: async () => (await accountingService.getAccountTags()).data,
		// Tags change rarely; cache generously so badges don't refetch per row.
		staleTime: 5 * 60 * 1000,
	});
}

export function useCreateAccountTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: Partial<AccountTag>) => accountingService.createAccountTag(body),
		onSuccess: () => { void qc.invalidateQueries({ queryKey: ACCOUNT_TAGS_KEY }); },
	});
}

export function useUpdateAccountTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Partial<AccountTag> }) => accountingService.updateAccountTag(id, body),
		onSuccess: () => { void qc.invalidateQueries({ queryKey: ACCOUNT_TAGS_KEY }); },
	});
}

export function useDeleteAccountTag() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => accountingService.deleteAccountTag(id),
		onSuccess: () => { void qc.invalidateQueries({ queryKey: ACCOUNT_TAGS_KEY }); },
	});
}
